import moment from 'moment-timezone';
import { isEqual, toUpper } from 'lodash';
import { isNullOrUndefined, isStringEmpty, isStringNotEmpty } from './objectUtils';

// the time zones an account can hold, stored by their USATimeZones names. a device reports a zone's canonical
// name - America/New_York - so a zone is matched by the offsets it keeps rather than by its name
export const ACCOUNT_TIME_ZONES = Object.freeze([
	Object.freeze({ value: 'US/Eastern', label: 'Eastern Time (ET)' }),
	Object.freeze({ value: 'US/Central', label: 'Central Time (CT)' }),
	Object.freeze({ value: 'US/Mountain', label: 'Mountain Time (MT)' }),
	Object.freeze({ value: 'US/Arizona', label: 'Arizona Time (MST)' }),
	Object.freeze({ value: 'US/Pacific', label: 'Pacific Time (PT)' }),
	Object.freeze({ value: 'US/Alaska', label: 'Alaska Time (AKT)' }),
	Object.freeze({ value: 'US/Hawaii', label: 'Hawaii Time (HT)' })
]);

// the system's own zone, which every scheduled job runs in: an account whose zone nothing names is kept in it
export const DEFAULT_ACCOUNT_TIME_ZONE = 'US/Pacific';

// the zone most of a state's people live in: a state split between two takes the larger side
const TIME_ZONE_BY_STATE = Object.freeze({
	AL: 'US/Central', AK: 'US/Alaska', AZ: 'US/Arizona', AR: 'US/Central', CA: 'US/Pacific', CO: 'US/Mountain',
	CT: 'US/Eastern', DE: 'US/Eastern', DC: 'US/Eastern', FL: 'US/Eastern', GA: 'US/Eastern', HI: 'US/Hawaii',
	ID: 'US/Mountain', IL: 'US/Central', IN: 'US/Eastern', IA: 'US/Central', KS: 'US/Central', KY: 'US/Eastern',
	LA: 'US/Central', ME: 'US/Eastern', MD: 'US/Eastern', MA: 'US/Eastern', MI: 'US/Eastern', MN: 'US/Central',
	MS: 'US/Central', MO: 'US/Central', MT: 'US/Mountain', NE: 'US/Central', NV: 'US/Pacific', NH: 'US/Eastern',
	NJ: 'US/Eastern', NM: 'US/Mountain', NY: 'US/Eastern', NC: 'US/Eastern', ND: 'US/Central', OH: 'US/Eastern',
	OK: 'US/Central', OR: 'US/Pacific', PA: 'US/Eastern', RI: 'US/Eastern', SC: 'US/Eastern', SD: 'US/Central',
	TN: 'US/Central', TX: 'US/Central', UT: 'US/Mountain', VT: 'US/Eastern', VA: 'US/Eastern', WA: 'US/Pacific',
	WV: 'US/Eastern', WI: 'US/Central', WY: 'US/Mountain'
});

// one offset in winter and one in summer tell standard time and daylight time apart
const getYearOffsets = (zone, year) => [Date.UTC(year, 0, 15), Date.UTC(year, 6, 15)].map((at) => zone.utcOffset(at));

/**
 * The account time zone that keeps the same clock as a zone, by any name moment-timezone knows: America/Phoenix
 * is US/Arizona and America/Detroit is US/Eastern. A zone no account time zone matches, such as one outside the
 * US, answers null.
 * @param {string|null} zoneName - an IANA zone name or alias
 * @returns {string|null} an ACCOUNT_TIME_ZONES value
 */
export const toAccountTimeZone = (zoneName) => {
	if (isStringEmpty(zoneName)) {
		return null;
	}
	const zone = moment.tz.zone(zoneName);
	if (isNullOrUndefined(zone)) {
		return null;
	}

	const year = new Date().getUTCFullYear();
	const offsets = getYearOffsets(zone, year);
	return ACCOUNT_TIME_ZONES.find(({ value }) => isEqual(getYearOffsets(moment.tz.zone(value), year), offsets))?.value ?? null;
};

/**
 * The account time zone for a US state, or DC.
 * @param {string|null} state - a two-letter state abbreviation
 * @returns {string|null} an ACCOUNT_TIME_ZONES value
 */
export const getTimeZoneForState = (state) => (isStringNotEmpty(state) ? TIME_ZONE_BY_STATE[toUpper(state.trim())] ?? null : null);

/**
 * The account time zone for the first address whose state names one.
 * @param {...(Object|null)} addresses - in the order they are trusted, such as shipping before billing
 * @returns {string|null} an ACCOUNT_TIME_ZONES value
 */
export const getTimeZoneForAddresses = (...addresses) => addresses.map((address) => getTimeZoneForState(address?.state)).find((timeZone) => isStringNotEmpty(timeZone)) ?? null;

/**
 * How an account time zone reads to a customer.
 * @param {string|null} timeZone - an ACCOUNT_TIME_ZONES value
 * @returns {string|null}
 */
export const getAccountTimeZoneLabel = (timeZone) => ACCOUNT_TIME_ZONES.find(({ value }) => value === timeZone)?.label ?? null;

/**
 * The account time zone of the device this runs on. IPQS reports the device's own setting as `device_timezone`;
 * its `timezone` is the ip address's, which a vpn moves, so it is not read.
 * @param {Object|null} deviceInfo - the IPQS device fingerprint result
 * @returns {string|null} an ACCOUNT_TIME_ZONES value
 */
export const getDeviceAccountTimeZone = (deviceInfo = null) => {
	const reportedTimeZone = toAccountTimeZone(deviceInfo?.device_timezone);
	if (isStringNotEmpty(reportedTimeZone)) {
		return reportedTimeZone;
	}

	try {
		return toAccountTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
	} catch (error) {
		return null;
	}
};
